<script>
export default {
	name: 'HistoryAnalytics',
	props: {
		// normalized shipment items
		items: {
			type: Array,
			default: () => [],
		},
		unit: {
			type: String,
			default: 'кг',
		},
	},
	computed: {
		totalOps() {
			return this.items.length
		},
		totalVolume() {
			const sum = this.items.reduce((acc, i) => acc + Number(i.total || 0), 0)
			return Math.round(sum * 100) / 100
		},
		outgoing() {
			return this.items.filter((i) => i.direction === 'out').length
		},
		incoming() {
			return this.items.filter((i) => i.direction === 'in').length
		},
		byStatus() {
			const map = {}
			this.items.forEach((i) => {
				const key = i.statusLabel || '-'
				map[key] = (map[key] || 0) + 1
			})
			return this.toBars(map)
		},
		byFish() {
			const map = {}
			this.items.forEach((i) => {
				;(i.fishes || []).forEach((f) => {
					const key = f.fish_name || f.title || '-'
					map[key] = (map[key] || 0) + Number(f.size || 0)
				})
			})
			return this.toBars(map, true)
		},
	},
	methods: {
		toBars(map, round) {
			const entries = Object.entries(map).map(([name, value]) => ({
				name,
				value: round ? Math.round(value * 100) / 100 : value,
			}))
			const max = Math.max(1, ...entries.map((e) => e.value))
			return entries
				.sort((a, b) => b.value - a.value)
				.map((e) => ({ ...e, percent: Math.round((e.value / max) * 100) }))
		},
	},
}
</script>

<template>
	<div class="analytics">
		<div class="analytics__stats">
			<div class="analytics__stat">
				<div class="analytics__stat-value">{{ totalOps }}</div>
				<div class="analytics__stat-label">
					{{ $t('virtual_warehouse.history.analytics_total_ops') }}
				</div>
			</div>
			<div class="analytics__stat">
				<div class="analytics__stat-value">{{ totalVolume }} {{ unit }}</div>
				<div class="analytics__stat-label">
					{{ $t('virtual_warehouse.history.analytics_total_volume') }}
				</div>
			</div>
			<div class="analytics__stat analytics__stat--blue">
				<div class="analytics__stat-value">{{ outgoing }}</div>
				<div class="analytics__stat-label">
					{{ $t('virtual_warehouse.history.analytics_outgoing') }}
				</div>
			</div>
			<div class="analytics__stat analytics__stat--green">
				<div class="analytics__stat-value">{{ incoming }}</div>
				<div class="analytics__stat-label">
					{{ $t('virtual_warehouse.history.analytics_incoming') }}
				</div>
			</div>
		</div>

		<div class="analytics__panels">
			<div class="analytics__panel">
				<div class="analytics__panel-title">
					{{ $t('virtual_warehouse.history.analytics_by_status') }}
				</div>
				<div v-for="(row, i) in byStatus" :key="'s' + i" class="analytics__row">
					<div class="analytics__row-head">
						<span>{{ row.name }}</span>
						<strong>{{ row.value }}</strong>
					</div>
					<div class="analytics__bar">
						<div class="analytics__bar-fill" :style="{ width: row.percent + '%' }"></div>
					</div>
				</div>
			</div>

			<div class="analytics__panel">
				<div class="analytics__panel-title">
					{{ $t('virtual_warehouse.history.analytics_by_fish') }}
				</div>
				<div v-for="(row, i) in byFish" :key="'f' + i" class="analytics__row">
					<div class="analytics__row-head">
						<span>{{ row.name }}</span>
						<strong>{{ row.value }} {{ unit }}</strong>
					</div>
					<div class="analytics__bar">
						<div
							class="analytics__bar-fill analytics__bar-fill--green"
							:style="{ width: row.percent + '%' }"
						></div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.analytics__stats {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 14px;
	margin-bottom: 18px;
}

.analytics__stat {
	background: #ffffff;
	border: 1px solid #eef2f8;
	border-radius: 16px;
	padding: 20px 22px;
	box-shadow: 0 4px 18px rgba(21, 60, 110, 0.05);
}

.analytics__stat-value {
	font-size: 30px;
	font-weight: 800;
	color: #1f2d49;
	line-height: 1;
}

.analytics__stat-label {
	margin-top: 8px;
	font-size: 13px;
	color: #8c9bb5;
}

.analytics__stat--blue .analytics__stat-value {
	color: #157fee;
}
.analytics__stat--green .analytics__stat-value {
	color: #1e8e4f;
}

.analytics__panels {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 14px;
}

.analytics__panel {
	background: #ffffff;
	border: 1px solid #eef2f8;
	border-radius: 16px;
	padding: 20px 22px;
	box-shadow: 0 4px 18px rgba(21, 60, 110, 0.05);
}

.analytics__panel-title {
	font-size: 14px;
	font-weight: 700;
	color: #2b3a55;
	margin-bottom: 16px;
}

.analytics__row {
	margin-bottom: 13px;
}

.analytics__row-head {
	display: flex;
	justify-content: space-between;
	font-size: 13px;
	color: #5a6b85;
	margin-bottom: 6px;
}
.analytics__row-head strong {
	color: #1f2d49;
}

.analytics__bar {
	height: 7px;
	border-radius: 999px;
	background: #e6eef8;
	overflow: hidden;
}

.analytics__bar-fill {
	height: 100%;
	border-radius: 999px;
	background: #157fee;
}
.analytics__bar-fill--green {
	background: #2bb673;
}

@media (max-width: 1024px) {
	.analytics__stats {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
	.analytics__panels {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 520px) {
	.analytics__stats {
		grid-template-columns: 1fr;
	}
}
</style>
